/**
 * Modules enforcement (PLAN-FASE3.md WP-06, closing the "Enforcement van
 * Modules-toggles" open point left by WP-00). WP-00 added the toggles in
 * Instellingen › Modules but toggling one off did nothing; this WP makes the
 * off state actually hide the nav item and block the route.
 *
 * The 8 fixed module keys, straight from `GET/PUT /companies/{id}/modules`
 * (WP-00). `ai_coach` has no dedicated page yet (metrics-only, per WP-00's
 * own note) — it stays a working toggle with no enforcement, same as today.
 */
export const MODULE_KEYS = [
  'vitality',
  'participation',
  'pilots',
  'challenges',
  'communication',
  'documents',
  'reports',
  'ai_coach',
] as const;

export type ModuleKey = (typeof MODULE_KEYS)[number];

/**
 * A module missing from the response reads as enabled — same default as
 * `SettingsModulesPage`'s `data[moduleKey] ?? true`, so a company that never
 * wrote a row for a module (or the fetch hasn't resolved yet) keeps every
 * page reachable rather than locking a company out by default.
 */
export function isModuleEnabled(
  modules: Readonly<Record<string, boolean>> | null,
  key: ModuleKey,
): boolean {
  return modules?.[key] ?? true;
}

/**
 * Maps each module-gated main-menu item (`src/editions/fit.ts`) to its
 * module key. Only the item ids that have a real page and a module toggle
 * are listed here — `overview`, `departments`, `users` have no module and
 * are never looked up. The nav item id matches its module key everywhere
 * except `communications` (item id, plural, matching the route) versus
 * `communication` (module key, singular, matching the API).
 */
export const ITEM_MODULES: Readonly<Record<string, ModuleKey>> = {
  vitality: 'vitality',
  participation: 'participation',
  pilots: 'pilots',
  challenges: 'challenges',
  communications: 'communication',
  documents: 'documents',
  reports: 'reports',
};

/** Same mapping, keyed by route path — for `ModuleRoute` in `src/app/routes.tsx`. */
export const ROUTE_MODULES: Readonly<Record<string, ModuleKey>> = {
  '/vitality': 'vitality',
  '/participation': 'participation',
  '/pilots': 'pilots',
  '/challenges': 'challenges',
  '/communications': 'communication',
  '/documents': 'documents',
  '/reports': 'reports',
};
